/**
 * The application: one screen, the roadmap.
 *
 * No router. There is exactly one view and a side panel, and react-router would add
 * a dependency, a history integration and a set of deep links to keep working
 * through CloudFront's SPA error-response rewrite - all to serve a single path. If a
 * second real page ever arrives, that is the moment to add it.
 *
 * TWO THINGS HERE ARE NOT OBVIOUS AND ARE DELIBERATE.
 *
 * 1. /api/me is consulted even though the user is already signed in. The Cognito
 *    pool is shared with the marketing compliance tool and API Gateway's authorizer
 *    accepts any token the pool ever issued, so a signed-in user may still be
 *    refused by the API. Decoding the token client-side would answer "what does this
 *    token claim"; only the endpoint answers "what does the API think", and those
 *    disagree exactly when it matters - a group added since sign-in, a stale token.
 *    The result is a specific explanation instead of nine empty lanes.
 *
 * 2. The visible span is recomputed from the projects in state rather than taken
 *    from the roadmap response's span_start/span_end. Editing a date changes the
 *    span, and the response fields are a snapshot from before the edit - trusting
 *    them means dragging a phase past the end of the chart and watching the bar
 *    vanish off the right-hand edge.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import GapsPanel from './components/GapsPanel';
import Legend from './components/Legend';
import LoginGate, { type AuthState } from './components/LoginGate';
import Timeline, { laneAnchorId } from './components/chart/Timeline';
import { describeError, getGaps, getIdentity, getRoadmap } from './services/api';
import { palette } from './styles/theme';
import {
  Chip,
  ErrorText,
  GlobalStyle,
  Panel,
  SecondaryButton,
  ToggleButton,
} from './styles/ui';
import type { Gap, GapReport, Identity, Phase, ProjectPatch, Roadmap } from './types';
import { buildGrid, formatLong, todayISO } from './utils/dates';

const Page = styled.div`
  max-width: 1500px;
  margin: 0 auto;
  padding: 20px 24px 48px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Masthead = styled.header`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  font-size: 26px;
  color: ${palette.deepMagenta};
`;

const Spacer = styled.div`
  flex: 1;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Columns = styled.div<{ $withPanel: boolean }>`
  display: grid;
  gap: 16px;
  grid-template-columns: ${(p) => (p.$withPanel ? 'minmax(0, 1fr) 360px' : 'minmax(0, 1fr)')};
  align-items: start;

  /* The side panel drops below the chart before the chart has to shrink: a Gantt
     narrower than its own minimum is unreadable, whereas a list of gaps reads fine
     at any width. */
  @media (max-width: 1180px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Status = styled.p`
  margin: 0;
  color: ${palette.inkSoft};
`;

const Denied = styled(Panel)`
  max-width: 620px;
  margin: 40px auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

/**
 * Named RoadmapScreen and not Roadmap because `Roadmap` is already the API response
 * type imported above, and a value and a type of the same name in one module is a
 * duplicate-identifier error rather than a shadow.
 */
function RoadmapScreen({ auth }: { auth: AuthState }) {
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [roadmap, setRoadmap] = useState<Roadmap | null>(null);
  const [gaps, setGaps] = useState<GapReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [showGaps, setShowGaps] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);

  // Captured once per mount. Recomputed on every render it would be a new string
  // each time, so every memo below - including the grid - would rebuild constantly.
  const today = useMemo(() => todayISO(), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const who = await getIdentity();
      setIdentity(who);
      if (!who.authorised) {
        // Stop here. Asking for the roadmap would 403 and replace a precise
        // explanation with a generic failure.
        return;
      }
      const [nextRoadmap, nextGaps] = await Promise.all([getRoadmap(includeInactive), getGaps()]);
      setRoadmap(nextRoadmap);
      setGaps(nextGaps);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Gaps change as a side effect of nearly every edit, so they are refreshed after one. */
  const refreshGaps = useCallback(async () => {
    try {
      setGaps(await getGaps());
    } catch {
      // A stale gap list is a much smaller problem than an error banner over a save
      // that actually succeeded. The next full load corrects it.
    }
  }, []);

  const onPhaseSaved = useCallback(
    (saved: Phase) => {
      setRoadmap((current) =>
        current
          ? {
              ...current,
              projects: current.projects.map((project) =>
                project.project_id === saved.project_id
                  ? {
                      ...project,
                      phases: project.phases.map((phase) =>
                        phase.phase_id === saved.phase_id ? saved : phase
                      ),
                    }
                  : project
              ),
            }
          : current
      );
      void refreshGaps();
    },
    [refreshGaps]
  );

  const onProjectSaved = useCallback(
    (projectId: string, patch: ProjectPatch) => {
      // Merged field by field. The PATCH response is ProjectOut and carries no
      // phases, so assigning it over the lane would empty it - the type forbids that
      // outright, and this is the reason why.
      setRoadmap((current) =>
        current
          ? {
              ...current,
              projects: current.projects.map((project) =>
                project.project_id === projectId ? { ...project, ...patch } : project
              ),
            }
          : current
      );
      void refreshGaps();
    },
    [refreshGaps]
  );

  const toggle = useCallback((projectId: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (!next.delete(projectId)) {
        next.add(projectId);
      }
      return next;
    });
  }, []);

  const projects = useMemo(() => {
    const list = roadmap?.projects ?? [];
    // lane_order is the workbook's own row order, which is the order everyone
    // already has in their head. Name is only the tiebreak.
    return [...list].sort((a, b) => a.lane_order - b.lane_order || a.name.localeCompare(b.name));
  }, [roadmap]);

  const grid = useMemo(() => {
    const dates = projects.flatMap((project) =>
      project.phases.flatMap((phase) =>
        [phase.start, phase.end].filter((d): d is string => d !== null)
      )
    );
    // Nothing scheduled anywhere: still draw a grid, centred on today, so the
    // toolbar and the lanes have something to sit against instead of collapsing.
    const start = dates.length ? dates.reduce((a, b) => (a < b ? a : b)) : today;
    const end = dates.length ? dates.reduce((a, b) => (a > b ? a : b)) : today;
    return buildGrid(start, end, today);
  }, [projects, today]);

  const allExpanded = projects.length > 0 && expanded.size === projects.length;

  const onGapSelected = useCallback((gap: Gap) => {
    setExpanded((current) => new Set(current).add(gap.project_id));
    // Deferred a frame: the lane's phase rows do not exist until the state above has
    // rendered, so scrolling immediately targets the collapsed row and lands short.
    requestAnimationFrame(() => {
      document
        .getElementById(laneAnchorId(gap.project_id))
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  }, []);

  if (identity && !identity.authorised) {
    return (
      <Denied>
        <h2>Not authorised for the planning roadmap</h2>
        <p>
          You are signed in as <strong>{identity.email ?? 'an unknown account'}</strong>, but the
          API requires membership of the <strong>{identity.required_group}</strong> group and this
          account is {identity.groups.length ? `in ${identity.groups.join(', ')}` : 'in no groups'}.
        </p>
        <p>
          The sign-in pool is shared with the marketing compliance tool, so a working password does
          not by itself grant access here. Ask an administrator to add you to the group.
        </p>
        {auth.signOut ? (
          <div>
            <SecondaryButton type="button" onClick={auth.signOut}>
              Sign out
            </SecondaryButton>
          </div>
        ) : null}
      </Denied>
    );
  }

  return (
    <Page>
      <Masthead>
        <Title>Planning Roadmap</Title>
        <Chip title={formatLong(today)}>Today {formatLong(today)}</Chip>
        {roadmap ? (
          <Chip>
            {projects.length} projects ·{' '}
            {projects.reduce((n, p) => n + p.phases.filter((ph) => !ph.structural).length, 0)}{' '}
            phases
          </Chip>
        ) : null}
        <Spacer />
        {identity?.email ? <Status>{identity.email}</Status> : null}
        {auth.signOut ? (
          <SecondaryButton type="button" onClick={auth.signOut}>
            Sign out
          </SecondaryButton>
        ) : null}
      </Masthead>

      <Toolbar>
        <SecondaryButton
          type="button"
          onClick={() =>
            setExpanded(allExpanded ? new Set() : new Set(projects.map((p) => p.project_id)))
          }
          disabled={projects.length === 0}
        >
          {allExpanded ? 'Collapse all' : 'Expand all'}
        </SecondaryButton>
        <ToggleButton
          type="button"
          $on={includeInactive}
          aria-pressed={includeInactive}
          onClick={() => setIncludeInactive((v) => !v)}
        >
          Include archived
        </ToggleButton>
        <ToggleButton
          type="button"
          $on={showGaps}
          aria-pressed={showGaps}
          onClick={() => setShowGaps((v) => !v)}
        >
          Still to decide{gaps ? ` (${gaps.gaps.length})` : ''}
        </ToggleButton>
        <SecondaryButton type="button" onClick={() => void load()} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </SecondaryButton>
        <Spacer />
        <Legend />
      </Toolbar>

      {error ? <ErrorText role="alert">{error}</ErrorText> : null}

      <Columns $withPanel={showGaps && gaps !== null}>
        <Panel>
          {loading && !roadmap ? (
            <Status>Loading the roadmap…</Status>
          ) : (
            <Timeline
              projects={projects}
              people={roadmap?.people ?? []}
              grid={grid}
              today={today}
              expanded={expanded}
              onToggle={toggle}
              onPhaseSaved={onPhaseSaved}
              onProjectSaved={onProjectSaved}
            />
          )}
        </Panel>

        {showGaps && gaps ? <GapsPanel report={gaps} onSelect={onGapSelected} /> : null}
      </Columns>
    </Page>
  );
}

export default function App() {
  return (
    <>
      <GlobalStyle />
      <LoginGate>{(auth) => <RoadmapScreen auth={auth} />}</LoginGate>
    </>
  );
}
